import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { onCaptureOrganizationSettled } from '../organize-client';
import { reportError } from '../../lib/monitoring';
import type { CaptureOrganizationRow, CaptureRow } from '../database.types';
import {
  hasActiveCaptureFilters,
  NO_CAPTURE_FILTERS,
  type CaptureFilters,
} from '../../features/captures/capture-filters';

/**
 * The Captures in one Project. M3 / S-03-02.
 *
 * UI → THIS HOOK → `.select()` (ARCHITECTURE §2). Reads go through select;
 * every write goes through a writer function (INV-T02). **This hook writes
 * nothing.**
 *
 * AUTHORIZATION IS RLS, NOT THE FILTER. `capture_select_own` only matches rows
 * the caller owns, so another user's Captures return zero rows even if their
 * Project id were somehow supplied. The `project_id` filter below scopes the
 * list to the Project being viewed; **it is not the security boundary and must
 * not be mistaken for one.** Both exist, and they are doing different jobs.
 *
 * There is deliberately no admin or service-role read anywhere in this file.
 */

export type CapturesStatus = 'loading' | 'ready' | 'failed';

/** A Capture plus the first line of its accepted transcript, for the list. */
export interface CaptureListItem {
  readonly capture: CaptureRow;
  /**
   * The CURRENT Final Accepted Transcript, or null when the Capture has none.
   *
   * **Read from `capture_transcript_version`, which is authoritative.** The
   * Work Log's `detail.transcript` is a QUOTE kept for provenance display and
   * is never the source — reading it here would be reading a copy that is
   * allowed to fall behind (D-016).
   *
   * **Current means the HIGHEST version, the same as Capture Detail.** The two
   * screens read the same Capture and must not disagree about what it says.
   */
  readonly transcript: string | null;
  /**
   * What Automatic Organization derived, or null when it has never run for
   * this Capture (M7 / S07-03).
   *
   * **Null is an ordinary state, not a failure** — every Capture saved before
   * S07-02 has no row, and the list must render those passively. Reading this
   * NEVER triggers organization: a screen that spent a model call because it
   * rendered would be a bill nobody asked for.
   */
  readonly organization: CaptureOrganizationRow | null;
}

export interface CapturesState {
  status: CapturesStatus;
  captures: CaptureListItem[];
  error: string | null;
  /** True while the list is narrowed, so the screen can say so honestly. */
  filtered: boolean;
  /**
   * True when the rows on screen are the database's CLOSE MATCHES rather than
   * exact ones (M8 / S08-03).
   *
   * **The database decides this, not the screen.** `search_captures` returns
   * `is_close_match` on every row, and a response is either all exact or all
   * close — never mixed — so a screen that inferred "these look approximate"
   * could be wrong in a way nobody would notice.
   */
  closeMatches: boolean;
  reload: () => Promise<void>;
}

/**
 * A read either produced items or it did not.
 *
 * **`reason` EXISTS SO A FAILURE CANNOT BE MISTAKEN FOR AN EMPTY RESULT.** The
 * one failure worth distinguishing is a Date filter asked for by a profile with
 * no canonical timezone: the database refuses rather than guessing (INV-T17),
 * and the screen has to say something true about that rather than showing an
 * unfiltered list under active-looking chips.
 */
type FetchResult =
  | { ok: true; items: CaptureListItem[]; closeMatches: boolean }
  | { ok: false; reason: 'timezone' | 'general' };

/**
 * SQLSTATE raised by `search_captures` when a Date filter is requested and the
 * profile carries no canonical timezone (migration 0013).
 */
const NO_TIMEZONE_CODE = 'CL006';

export function useCaptures(
  projectId: string | undefined,
  filters: CaptureFilters = NO_CAPTURE_FILTERS,
): CapturesState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<CapturesStatus>(client ? 'loading' : 'failed');
  const [captures, setCaptures] = useState<CaptureListItem[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  // Destructured so the callback below depends on three primitives rather than
  // on an object identity, which a caller re-creating `{...}` every render
  // would otherwise change on every render.
  const { query, locationKey, date, status: statusFilter } = filters;
  const narrowing = hasActiveCaptureFilters({ query, locationKey, date, status: statusFilter });
  const [closeMatches, setCloseMatches] = useState(false);

  /**
   * **THE STALE-RESPONSE GUARD, AND IT IS NOT OPTIONAL WITH SEARCH-AS-YOU-TYPE.**
   *
   * Typing `d`, `dr`, `dry`, `dryw`, `drywal` can put several reads in flight at
   * once, and they do not come back in the order they left. Without this, a
   * slow answer for `dry` can land AFTER the answer for `drywal` and overwrite
   * it — leaving results on screen that belong to a query Brandon has already
   * finished typing, with no error and nothing to suggest anything is wrong.
   *
   * Every fetch takes a ticket. Only the newest ticket may write to state.
   */
  const generation = useRef(0);

  const fetchCaptures = useCallback(async (): Promise<FetchResult> => {
    if (!client || !projectId) return { ok: true, items: [], closeMatches: false };

    /**
     * **THE DATABASE DECIDES WHICH CAPTURES, ALWAYS.**
     *
     * When a filter is active the ids come from `search_captures` — the one
     * retrieval boundary S08-01 built — and this hook only hydrates them.
     * There is deliberately **no client-side filtering engine**: a second
     * implementation of "which Captures match" would drift from the first, and
     * the drift would look like missing Captures rather than like a bug.
     *
     * The RPC also owns the Date boundary, resolved in the canonical Crewlink
     * timezone. Nothing here computes a day (INV-T17).
     */
    let orderedIds: string[] | null = null;
    let close = false;

    if (narrowing) {
      const { data: hits, error: searchError } = await client.rpc('search_captures', {
        // **EXPLICIT PROJECT SCOPE** (migration 0015). The Captures screen is
        // one Project's list and nothing else — Sol's global retrieval is a
        // different caller with a different scope. Stating it here is what
        // stops a dropped `p_project_id` ever quietly becoming a search across
        // every Project Brandon owns.
        p_scope: 'project',
        p_project_id: projectId,
        // The Search text, or null when the box is empty. The database owns
        // strict-first retrieval and the close-match fallback; **nothing here
        // decides which one ran** — it reads `is_close_match` off the answer.
        p_query: query.trim() === '' ? null : query,
        p_location_key: locationKey,
        p_status: statusFilter,
        p_date: date,
      });

      if (searchError) {
        reportError(searchError, 'captures.filter');
        const code = (searchError as { code?: string }).code;
        const message = (searchError as { message?: string }).message ?? '';
        return {
          ok: false,
          reason:
            code === NO_TIMEZONE_CODE || message.includes(NO_TIMEZONE_CODE)
              ? 'timezone'
              : 'general',
        };
      }

      const rows = hits ?? [];
      orderedIds = rows.map((hit) => hit.capture_id);
      // A response is either all exact or all close, so the first row speaks
      // for the set. An empty set is neither.
      close = rows.length > 0 && rows[0]?.is_close_match === true;
      // No matches is an ANSWER, not a failure — and it must not fall through
      // to the unfiltered read below, which would show everything under active
      // chips.
      if (orderedIds.length === 0) return { ok: true, items: [], closeMatches: false };
    }

    // ORDERED BY capture_number, NOT created_at. Two Captures saved in the same
    // second would tie on a timestamp and the order would be undefined —
    // `unique(project_id, capture_number)` makes this total and deterministic,
    // which is what "newest first, always" actually requires.
    //
    // The `project_id` filter stays on BOTH paths. On the filtered path the RPC
    // has already scoped the ids, and keeping it here costs nothing while
    // making the scope visible at the point the rows are actually read.
    const captureQuery = client.from('capture').select('*').eq('project_id', projectId);
    const { data, error: queryError } = await (orderedIds
      ? captureQuery.in('id', orderedIds)
      : captureQuery.order('capture_number', { ascending: false }));

    if (queryError) {
      reportError(queryError, 'captures.load');
      return { ok: false, reason: 'general' };
    }

    const captureRows = data ?? [];
    if (captureRows.length === 0) return { ok: true, items: [], closeMatches: false };

    // A SECOND QUERY rather than an embedded join, deliberately. The hand-written
    // database types declare no relationships, so an embed would silently
    // degrade the whole schema to `any` — losing type safety across every other
    // query in the app to save one round trip on a list this size.
    const { data: versions, error: versionError } = await client
      .from('capture_transcript_version')
      .select('capture_id, version, body')
      .in(
        'capture_id',
        captureRows.map((row) => row.id),
      );

    if (versionError) {
      reportError(versionError, 'captures.transcripts');
      return { ok: false, reason: 'general' };
    }

    // THE CURRENT TRANSCRIPT PER CAPTURE — the HIGHEST version of each, chosen
    // INDEPENDENTLY.
    //
    // This query used to end in `.eq('version', 1)`, which was right until
    // M6 / S06-03 made transcripts editable. `use-capture.ts` was corrected
    // then and this was missed, so a corrected Capture read correctly on
    // Detail and still showed its uncorrected words in this list.
    //
    // **THE OBVIOUS REPAIR IS WRONG, AND SUBTLY.** Dropping the filter and
    // adding `.order('version', { ascending: false })` would leave the old
    // `new Map(rows.map(...))` below picking the LAST row per key — Map keeps
    // the last entry, so newest-first ordering yields the OLDEST version. The
    // reverse ordering happens to work, which is worse: correctness would rest
    // on a sort direction and a Map subtlety agreeing, and one query rows
    // arrive in a different order breaks the list with nothing failing.
    //
    // So the maximum is computed HERE, by comparing version numbers, and this
    // loop is correct **whatever order the rows arrive in** — including a
    // batch where one Capture's rows are ascending and another's are not.
    const currentByCapture = new Map<string, { version: number; body: string }>();
    for (const row of versions ?? []) {
      const held = currentByCapture.get(row.capture_id);
      if (!held || row.version > held.version) {
        currentByCapture.set(row.capture_id, { version: row.version, body: row.body });
      }
    }
    const byCapture = new Map([...currentByCapture].map(([id, row]) => [id, row.body]));

    // ONE BATCHED QUERY FOR THE WHOLE PAGE, not one per Capture.
    //
    // An embedded PostgREST relation would be fewer round trips still, but the
    // hand-written database types declare no relationships — the comment above
    // the transcript query records why that matters: an embed silently degrades
    // the whole schema to `any`, losing type safety across every other query in
    // the app. A third `.in(...)` costs one request and keeps the types honest.
    //
    // **READING THIS NEVER ORGANIZES ANYTHING.** No writer is called here and
    // no endpoint is reached; a Capture with no row simply has none.
    const { data: organizationRows, error: organizationError } = await client
      .from('capture_organization')
      .select('*')
      .in(
        'capture_id',
        captureRows.map((row) => row.id),
      );

    if (organizationError) {
      reportError(organizationError, 'captures.organization');
      return { ok: false, reason: 'general' };
    }

    const organizationByCapture = new Map(
      (organizationRows ?? []).map((row) => [row.capture_id, row]),
    );

    const items = captureRows.map((capture) => ({
      capture,
      // Absent is a REAL state, not a missing read: a Capture without a
      // transcript is valid (PT §5) and nothing may assume one exists.
      transcript: byCapture.get(capture.id) ?? null,
      organization: organizationByCapture.get(capture.id) ?? null,
    }));

    // **THE DATABASE'S ORDER IS RESTORED, NOT RE-DERIVED.** `.in(...)` returns
    // rows in whatever order PostgREST likes; the RPC already decided the
    // order, and re-sorting here would be this hook quietly disagreeing with
    // the retrieval boundary about which Capture comes first.
    if (orderedIds) {
      const position = new Map(orderedIds.map((id, index) => [id, index]));
      items.sort(
        (left, right) =>
          (position.get(left.capture.id) ?? 0) - (position.get(right.capture.id) ?? 0),
      );
    }

    return { ok: true, items, closeMatches: close };
  }, [client, projectId, narrowing, query, locationKey, date, statusFilter]);

  const applyResult = useCallback((result: FetchResult) => {
    if (!result.ok) {
      // **NEVER FALL BACK TO AN UNFILTERED LIST.** Showing every Capture under
      // chips that look active would be a confident lie about what matched.
      setCaptures([]);
      setCloseMatches(false);
      setError(
        result.reason === 'timezone'
          ? 'Crewlink needs your timezone before Date filters can be used.'
          : 'Could not load Captures. Check your connection and try again.',
      );
      setStatus('failed');
      return;
    }
    setCaptures(result.items);
    setCloseMatches(result.closeMatches);
    setError(null);
    setStatus('ready');
  }, []);

  /**
   * FETCH, AND APPLY ONLY IF THIS IS STILL THE NEWEST REQUEST.
   *
   * **Every read path goes through here**, so the stale-response guard cannot
   * be forgotten by whichever path someone adds next. A read that took its
   * ticket before a newer one started is allowed to finish — it simply is not
   * allowed to speak.
   */
  const fetchAndApply = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchCaptures();
    // A newer query has started since this one left. Its answer is the one
    // Brandon is waiting for; this one is about a question he has moved on
    // from, and writing it would put the wrong results under the right box.
    if (ticket !== generation.current) return;
    applyResult(result);
  }, [fetchCaptures, applyResult]);

  useEffect(() => {
    void fetchAndApply();
  }, [fetchAndApply]);

  const reload = useCallback(async () => {
    await fetchAndApply();
  }, [fetchAndApply]);

  /**
   * CATCH UP WHEN A BACKGROUND ORGANIZATION ATTEMPT FINISHES — M7 post-closeout.
   *
   * The Capture list carries the location in every title, so it goes stale for
   * exactly the same reason Capture Detail did: a Capture saved seconds ago is
   * organized a few seconds later, and this list had already read the row
   * before that happened.
   *
   * **IT RELOADS FOR ANY SETTLED CAPTURE, NOT ONLY ONE ALREADY IN ITS ROWS**,
   * and that is deliberate rather than lazy. The stale case that matters most
   * is a Capture created moments ago: the list can still be mid-fetch when the
   * attempt settles, so "is this id in my current rows?" is a question with a
   * racy answer at precisely the moment it needs a correct one. At most one
   * Capture list is mounted at a time, and the cost of being wrong is one
   * `.select()` too many — against a title that stays wrong until Brandon
   * navigates away and back.
   *
   * **THIS RE-READS AND NOTHING ELSE.** No writer runs, no endpoint is reached,
   * and no provider call can originate here, so a refresh cannot trigger a
   * second reasoning request or make an AI-derived value human-owned.
   */
  useEffect(() => {
    const unsubscribe = onCaptureOrganizationSettled(() => {
      // Through the same guarded path: a background refresh must not overwrite
      // the answer to a Search the person is in the middle of typing either.
      void fetchAndApply();
    });
    return unsubscribe;
  }, [fetchAndApply]);

  return { status, captures, error, filtered: narrowing, closeMatches, reload };
}
